import { type IdMeAuthorizationResult } from '../../idme.types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * NOTE: `promptAsync` checks `state` on web; the WebView sees raw navigations, so it's checked here.
 */
export function readIdMeAuthorizationResponse(params: Params): AuthorizationResponse {
  const { url, redirectUri, state } = params;

  const isRedirectUrl = checkIfRedirectUrl({ url, redirectUri });

  if (!isRedirectUrl) {
    return {
      status: 'pending',
    };
  }

  const { searchParams } = new URL(url);

  const error = searchParams.get('error');
  const code = searchParams.get('code');

  if (error) {
    return {
      status: 'failed',
      code: error,
      description: searchParams.get('error_description') ?? '',
    };
  }

  if (searchParams.get('state') !== state) {
    return {
      status: 'failed',
      code: 'state_mismatch',
      description: '',
    };
  }

  if (!code) {
    return {
      status: 'failed',
      code: 'no_authorization_code',
      description: '',
    };
  }

  return {
    status: 'authorized',
    code,
  };
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function checkIfRedirectUrl(params: CheckIfRedirectUrlParams) {
  const { url, redirectUri } = params;

  try {
    const navigatedUrl = new URL(url);
    const redirectUrl = new URL(redirectUri);

    const isSameOrigin = navigatedUrl.origin === redirectUrl.origin;
    const isSamePath = navigatedUrl.pathname === redirectUrl.pathname;

    return isSameOrigin && isSamePath;
  } catch {
    return false;
  }
}

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  url: string;
  redirectUri: string;
  state: string;
};

type CheckIfRedirectUrlParams = Pick<Params, 'url' | 'redirectUri'>;

type AuthorizationResponse = IdMeAuthorizationResult | { status: 'pending' };
